// iter683 — Twin Network Force-Directed Graph page.
// Interactive D3-force visualisation of the 18 twins + 75 cross-twin
// physiological rules. Active rules (firing right now for this user)
// glow and pulse. Click an edge to see its evidence + recommended actions.
//
// Built with d3-force (~25kb) + plain SVG — no extra heavy viz lib.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
} from "d3-force";
import { useTheme } from "@/theme/ThemeContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Activity, Sparkles, Target } from "lucide-react";
import EducationalRibbon from "@/components/EducationalRibbon";
import AdaptiveGroup from "@/components/dashboard/AdaptiveGroup";  // iter1077
import useIsDesktop from "@/hooks/useIsDesktop";  // iter1077
import PhysiologyBridgeCard from "@/components/PhysiologyBridgeCard";
import CrossTwinCorrelationsCard from "@/components/twins/CrossTwinCorrelationsCard";
import TwinChatPanel from "@/components/twin_chat/TwinChatPanel";
import WeeklyGoalsCard from "@/components/goals/WeeklyGoalsCard";
import LockedFeatureDialog from "@/components/LockedFeatureDialog";
import { TWIN_CATALOG } from "@/lib/twinCatalog";

// iter1090 — Кой раздел от профила отключва всеки близнак. Използва се
// от LockedFeatureDialog в locked node CTA.
const TWIN_REQUIRED_SECTION = {
  nutritional: "basics", lab: "labs", phenotype: "conditions",
  microbiome: "lifestyle", body_comp: "basics", sleep: "lifestyle",
  stress: "lifestyle", cardio: "basics", hormonal: "cycle_perinatal",
  metabolic_insulin: "labs", inflammation: "labs", movement: "lifestyle",
  liver_kidney: "labs", kidney: "labs", dental: "conditions",
  bhi: "cycle_perinatal", hematology: "labs", neuro: "lifestyle",
  male_health: "labs", respiratory: "lifestyle", thyroid: "labs",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category palette — gives the graph visual rhythm
const CATEGORY_COLORS = {
  intake:    "#10b981",
  mind:      "#8b5cf6",
  biomarker: "#3b82f6",
  body:      "#f59e0b",
  recovery:  "#06b6d4",
  hormones:  "#ec4899",
};

function moodColor(score) {
  if (score === null || score === undefined) return "#94a3b8";
  if (score >= 80) return "#10b981";
  if (score >= 65) return "#3b82f6";
  if (score >= 50) return "#f59e0b";
  return "#f43f5e";
}

export default function TwinNetworkPage() {
  const { isDark } = useTheme();
  const isDesktop = useIsDesktop(768);  // iter1077 — сгъваеми раздели на телефон
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [layout, setLayout] = useState({ nodes: [], edges: [] });
  // iter1090 — Локиран модал за unlock explainer
  const [lockedDialogOpen, setLockedDialogOpen] = useState(false);
  const [lockedDialogTwin, setLockedDialogTwin] = useState(null);
  // iter1045 — фокус върху един близнак: ?twin=<id> показва само неговите връзки
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("twin");

  const width = 1000;
  const height = 640;

  // Fetch
  useEffect(() => {
    axios
      .get(`${API}/health-twin/network`, { timeout: 45000 })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.message || "Network fetch failed"));
  }, []);

  // Force simulation — runs once data loads (iter1045 — re-runs on focus change)
  useEffect(() => {
    if (!data?.nodes?.length) return;
    const allEdges = data.edges || [];
    const focusEdges = focusId
      ? allEdges.filter((e) => e.source === focusId || e.target === focusId)
      : allEdges;
    const keepIds = focusId
      ? new Set([focusId, ...focusEdges.flatMap((e) => [e.source, e.target])])
      : null;
    const nodes = data.nodes
      .filter((n) => !keepIds || keepIds.has(n.id))
      .map((n) => ({ ...n }));
    const links = focusEdges.map((e) => ({
      ...e,
      source: e.source,
      target: e.target,
    }));

    const sim = forceSimulation(nodes)
      .force(
        "link",
        forceLink(links)
          .id((d) => d.id)
          .distance((l) => (l.firing ? 90 : 130))
          .strength((l) => (l.firing ? 0.7 : 0.25))
      )
      .force("charge", forceManyBody().strength(-320))
      .force("center", forceCenter(width / 2, height / 2))
      .force("collide", forceCollide(38))
      .alpha(1)
      .alphaDecay(0.025);

    let frame = 0;
    const onTick = () => {
      frame += 1;
      // Only push to React every 3rd tick to keep render cost manageable
      if (frame % 3 === 0) {
        setLayout({ nodes: [...nodes], edges: [...links] });
      }
    };
    sim.on("tick", onTick).on("end", () => {
      setLayout({ nodes: [...nodes], edges: [...links] });
    });

    return () => { sim.stop(); };
  }, [data, focusId]);

  // iter1045 — при фокус отваряме веднага и панела с детайлите на близнака
  useEffect(() => {
    if (!focusId || !data?.nodes?.length) return;
    const n = data.nodes.find((x) => x.id === focusId);
    if (n) { setSelectedNode(n); setSelectedEdge(null); }
  }, [focusId, data]);

  const focusNode = focusId ? (data?.nodes || []).find((n) => n.id === focusId) : null;
  const focusEdgeCount = focusId
    ? (data?.edges || []).filter((e) => e.source === focusId || e.target === focusId).length
    : 0;
  const focusFiringCount = focusId
    ? (data?.edges || []).filter(
        (e) => (e.source === focusId || e.target === focusId) && e.firing
      ).length
    : 0;

  const activeCount = data?.active_count ?? 0;
  const totalEdges = data?.total_edges ?? 0;
  const unlockedCount = data?.unlocked_count ?? 0;
  const totalTwins = data?.total_twins ?? 0;
  const coverage = data?.coverage ?? null;

  return (
    <div
      data-testid="twin-network-page"
      className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}
    >
      {/* Header */}
      <div className={`px-5 py-3 border-b sticky top-0 z-10 backdrop-blur ${
        isDark ? "bg-slate-950/80 border-slate-800" : "bg-white/80 border-slate-200"
      }`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="twin-network-back"
            className="flex items-center gap-1.5 text-sm hover:opacity-80"
          >
            <ArrowLeft className="w-4 h-4" />
            Назад към таблото
          </button>
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => navigate("/dashboard/marker-map")}
              data-testid="twin-network-marker-map-btn"
              className="px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-500 hover:opacity-80"
              title="Кой близнак оценява кой показател"
            >
              Карта на показателите
            </button>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500"
              data-testid="twin-network-unlocked-counter"
              title="Активни близнаци (с данни за score)"
            >
              🔓 {unlockedCount}/{totalTwins} активни
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-500">
              <Activity className="w-3 h-3 animate-pulse" />
              {activeCount} връзки
            </span>
            <span className="opacity-60">от {totalEdges}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-5">
        <EducationalRibbon
          lang="bg"
          variant="banner"
          isDark={isDark}
          className="mb-4"
          testId="twin-network-educational-ribbon"
        />
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold">Жива физиологична мрежа</h1>
            <p className="text-sm opacity-70 mt-1 max-w-2xl">
              {totalTwins} дигитални близнаци и {totalEdges} физиологични връзки между тях.
              Светещите линии са връзки, които работят за теб точно сега. Кликни на връзка
              за подробности и препоръка.
            </p>
          </div>
          <button
            onClick={() => navigate("/phenotype-explorer")}
            data-testid="open-phenotype-explorer"
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
              isDark
                ? "bg-gradient-to-r from-pink-500/20 to-violet-500/20 hover:from-pink-500/30 hover:to-violet-500/30 text-pink-200 border border-pink-500/30"
                : "bg-gradient-to-r from-pink-50 to-violet-50 hover:from-pink-100 hover:to-violet-100 text-pink-700 border border-pink-200"
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            Phenotype Explorer
          </button>
        </div>

        {error && (
          <div className="p-4 rounded-lg bg-rose-50 text-rose-700 text-sm">{error}</div>
        )}

        {/* iter1039 — покритие на мрежата: има ли близнак без връзка */}
        {coverage && (
          <div
            data-testid="twin-network-coverage"
            className={`mb-3 p-3 rounded-xl border text-[12px] ${
              isDark ? "bg-slate-900/40 border-slate-800" : "bg-white border-slate-200"
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="font-bold" data-testid="twin-network-coverage-counts">
                {coverage.twins} близнака · {coverage.edges} връзки · {coverage.firing} активни сега
              </span>
              <span className="opacity-70">
                свързани: {coverage.linked_twins}/{coverage.twins} · с данни: {coverage.unlocked}/{coverage.twins}
              </span>
            </div>
            <p className="opacity-75 mt-1" data-testid="twin-network-coverage-note">
              {coverage.note_bg}
              {coverage.isolated?.length > 0 && (
                <span className="font-semibold text-amber-500">
                  {" "}Без връзка: {coverage.isolated.map((n) => n.label).join(", ")}.
                </span>
              )}
            </p>
          </div>
        )}

        {/* iter1045 — фокус върху един близнак */}
        {focusId && (
          <div
            data-testid="twin-network-focus-banner"
            className={`mb-3 p-3 rounded-xl border flex flex-wrap items-center gap-2 ${
              isDark ? "bg-indigo-500/10 border-indigo-500/30" : "bg-indigo-50 border-indigo-200"
            }`}
          >
            <span className="text-[12px] font-bold" data-testid="twin-network-focus-label">
              {focusNode?.emoji ? `${focusNode.emoji} ` : ""}
              {focusNode?.label || TWIN_CATALOG[focusId]?.label || focusId}
            </span>
            <span className="text-[11px] opacity-75" data-testid="twin-network-focus-counts">
              {focusEdgeCount} връзки · {focusFiringCount} активни сега
            </span>
            {TWIN_CATALOG[focusId]?.route && (
              <button
                onClick={() => navigate(TWIN_CATALOG[focusId].route)}
                data-testid="twin-network-focus-open-module"
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors ${
                  isDark ? "bg-indigo-500/25 text-indigo-100 hover:bg-indigo-500/35"
                         : "bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-100"
                }`}
              >
                Отвори модула →
              </button>
            )}
            <button
              onClick={() => setSearchParams({})}
              data-testid="twin-network-focus-clear"
              className="ml-auto text-[11px] opacity-70 hover:opacity-100 underline"
            >
              Виж всички връзки
            </button>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px]">
          {Object.entries(CATEGORY_COLORS).map(([cat, col]) => (
            <span key={cat} className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: col }} />
              <span className="opacity-70 capitalize">{cat}</span>
            </span>
          ))}
          <span className="ml-auto flex items-center gap-1.5 opacity-70">
            <Sparkles className="w-3 h-3 text-rose-500" />
            пулсиращите ръбове са активни правила
          </span>
        </div>

        {/* SVG Graph — iter1077: на телефон картата се показва в естествения си
            мащаб с хоризонтално плъзгане; при 358 px надписите падаха до
            3.6 px и бяха нечетими. */}
        {!isDesktop && (
          <p className={`text-[11px] mb-1.5 px-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}
             data-testid="twin-network-pan-hint">
            Плъзнете картата наляво и надясно, за да видите всички близнаци.
          </p>
        )}
        <div
          data-testid="twin-network-svg-container"
          className={`rounded-2xl border ${isDesktop ? "overflow-hidden" : "overflow-x-auto"} ${
            isDark ? "bg-slate-900/40 border-slate-800" : "bg-white border-slate-200"
          }`}
        >
          <svg
            ref={svgRef}
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-auto block"
            style={isDesktop ? undefined : { minWidth: 1100 }}
          >
            <defs>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Edges first (behind nodes) */}
            {layout.edges.map((e) => {
              const sx = e.source.x, sy = e.source.y;
              const tx = e.target.x, ty = e.target.y;
              if (sx === undefined || tx === undefined) return null;
              const isHover = selectedEdge?.id === e.id;
              const severity = e.severity || "watch";
              const stroke = e.firing
                ? severity === "action" ? "#f43f5e" : "#f59e0b"
                : isDark ? "#334155" : "#cbd5e1";
              return (
                <line
                  key={e.id}
                  data-testid={`twin-network-edge-${e.id}`}
                  x1={sx} y1={sy} x2={tx} y2={ty}
                  stroke={stroke}
                  strokeWidth={e.firing ? 2.5 : 1}
                  strokeOpacity={e.firing ? 0.9 : 0.4}
                  filter={e.firing ? "url(#glow)" : undefined}
                  style={{ cursor: "pointer", transition: "stroke-width 0.2s" }}
                  onClick={() => { setSelectedEdge(e); setSelectedNode(null); }}
                >
                  {e.firing && (
                    <animate
                      attributeName="stroke-opacity"
                      values="0.9;0.4;0.9"
                      dur="2.2s"
                      repeatCount="indefinite"
                    />
                  )}
                </line>
              );
            })}

            {/* Nodes */}
            {layout.nodes.map((n) => {
              const x = n.x, y = n.y;
              if (x === undefined) return null;
              const cat = CATEGORY_COLORS[n.category] || "#64748b";
              const mood = moodColor(n.score);
              const isSelected = selectedNode?.id === n.id;
              // iter895 — Progressive-disclosure: dim locked twins so users
              // aren't overwhelmed by 18 identical circles. Locked = no data
              // yet; the detail panel shows the CTA to unlock.
              const isLocked = n.unlocked === false;
              return (
                <g
                  key={n.id}
                  transform={`translate(${x}, ${y})`}
                  data-testid={`twin-network-node-${n.id}`}
                  data-locked={isLocked ? "true" : "false"}
                  style={{ cursor: "pointer", opacity: isLocked ? 0.35 : 1 }}
                  onClick={() => { setSelectedNode(n); setSelectedEdge(null); }}
                >
                  <circle r={isSelected ? 30 : 26} fill={cat} fillOpacity={isLocked ? 0.05 : 0.15} />
                  <circle
                    r={isSelected ? 24 : 20}
                    fill={isLocked ? "#94a3b8" : mood}
                    stroke={cat}
                    strokeWidth={2.5}
                    strokeDasharray={isLocked ? "3 3" : undefined}
                  />
                  <text textAnchor="middle" dy="0.35em" fontSize="16">{isLocked ? "🔒" : n.emoji}</text>
                  <text textAnchor="middle" y={38} fontSize="10" fontWeight="600" fill={isDark ? "#cbd5e1" : "#334155"}>
                    {n.label}
                  </text>
                  {typeof n.score === "number" && (
                    <text textAnchor="middle" y={50} fontSize="9" fill={isDark ? "#94a3b8" : "#64748b"}>
                      {n.score}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Detail panel */}
        {selectedEdge && (
          <div
            data-testid="twin-network-edge-detail"
            className={`mt-4 p-4 rounded-xl border ${
              isDark
                ? "bg-amber-500/10 border-amber-500/30"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase font-bold opacity-70">
                {selectedEdge.category_bg} · {selectedEdge.firing ? "АКТИВНА" : "СПЯЩА"}
              </div>
              <button
                onClick={() => setSelectedEdge(null)}
                className="text-xs opacity-60 hover:opacity-100"
              >
                ✕ затвори
              </button>
            </div>
            <div className="font-semibold text-sm mb-1">{selectedEdge.title_bg}</div>
            <p className="text-xs opacity-80 leading-relaxed mb-2">{selectedEdge.message_bg}</p>
            {selectedEdge.action_bg && (
              <div className={`text-xs mt-2 p-2 rounded ${
                isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"
              }`}>
                <strong>Действие:</strong> {selectedEdge.action_bg}
              </div>
            )}
            {selectedEdge.evidence_bg && (
              <div className="text-[10px] italic opacity-60 mt-2">
                📚 {selectedEdge.evidence_bg}
              </div>
            )}
          </div>
        )}

        {selectedNode && (          <div
            data-testid="twin-network-node-detail"
            className={`mt-4 p-4 rounded-xl border ${
              isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase font-bold opacity-70">{selectedNode.category}</div>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-xs opacity-60 hover:opacity-100"
              >
                ✕ затвори
              </button>
            </div>
            <div className="text-lg font-semibold flex items-center gap-2 mb-1">
              <span>{selectedNode.emoji}</span> {selectedNode.label}
            </div>
            {selectedNode.unlocked === false ? (
              <div
                className={`mt-2 p-2.5 rounded-lg text-xs leading-relaxed ${
                  isDark ? "bg-amber-500/10 text-amber-200 border border-amber-500/30"
                         : "bg-amber-50 text-amber-800 border border-amber-200"
                }`}
                data-testid="twin-network-node-locked-hint"
              >
                🔒 <strong>Още не е активиран.</strong>
                <div className="mt-1 opacity-90">{selectedNode.unlock_hint_bg || "Добавете данни за да отключите."}</div>
                <button
                  type="button"
                  onClick={() => {
                    setLockedDialogTwin(selectedNode);
                    setLockedDialogOpen(true);
                  }}
                  data-testid="twin-network-node-unlock-explainer"
                  className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                    isDark ? "bg-amber-500/20 text-amber-100 hover:bg-amber-500/30"
                           : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                  }`}
                >
                  Какво трябва да попълня?
                </button>
              </div>
            ) : (
              <div className="text-xs opacity-80">
                Текущ резултат:{" "}
                <strong className="text-base" style={{ color: moodColor(selectedNode.score) }}>
                  {selectedNode.score ?? "—"}
                </strong>
                {typeof selectedNode.score === "number" && <span className="opacity-50"> / 100</span>}
              </div>
            )}
            {TWIN_CATALOG[selectedNode.id]?.route && (
              <button
                onClick={() => navigate(TWIN_CATALOG[selectedNode.id].route)}
                data-testid="twin-network-node-open-module"
                className={`mt-2.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors ${
                  isDark ? "bg-indigo-500/20 text-indigo-200 hover:bg-indigo-500/30"
                         : "bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-50"
                }`}
              >
                Отвори модула →
              </button>
            )}
            {!focusId && (
              <button
                onClick={() => setSearchParams({ twin: selectedNode.id })}
                data-testid="twin-network-node-focus"
                className="mt-2.5 ml-2 text-[11px] opacity-70 hover:opacity-100 underline"
              >
                Само връзките на този близнак
              </button>
            )}
          </div>
        )}
        {/* iter1030 — всичко за връзките между близнаците е тук:
            общата физиология + корелациите за 60 дни.
            iter1077 — на телефон долните три раздела са сгъваеми: страницата
            беше над 14 000 px висока и се превърташе безкрайно. */}
        {/* iter1064 — разговор с цялата мрежа, върху същите данни */}
        <div className="mt-4">
          <TwinChatPanel />
        </div>
        {/* iter1066 — целите, записани от разговора, стоят до него */}
        <div className="mt-4 space-y-3">
          <AdaptiveGroup
            plain={isDesktop}
            title="Моите цели за седмицата"
            icon={Target}
            color="#10B981"
            storageKey="twinnet-goals"
            testId="mobile-fold-twinnet-goals"
          >
            <WeeklyGoalsCard />
          </AdaptiveGroup>
          <AdaptiveGroup
            plain={isDesktop}
            title="Физиологичен мост"
            icon={Activity}
            color="#0EA5E9"
            storageKey="twinnet-bridge"
            testId="mobile-fold-twinnet-bridge"
          >
            <PhysiologyBridgeCard isDark={isDark} />
          </AdaptiveGroup>
          <AdaptiveGroup
            plain={isDesktop}
            title="Връзки между близнаците за 60 дни"
            icon={Sparkles}
            color="#8B5CF6"
            storageKey="twinnet-correlations"
            testId="mobile-fold-twinnet-correlations"
          >
            <CrossTwinCorrelationsCard />
          </AdaptiveGroup>
        </div>
      </div>

      {/* iter1090 — LockedFeatureDialog за близнаци, които още не са отключени */}
      <LockedFeatureDialog
        open={lockedDialogOpen}
        onOpenChange={setLockedDialogOpen}
        featureName={lockedDialogTwin ? `${lockedDialogTwin.emoji || ""} ${lockedDialogTwin.label}`.trim() : null}
        requiredSectionKey={lockedDialogTwin ? TWIN_REQUIRED_SECTION[lockedDialogTwin.id] : null}
      />
    </div>
  );
}
